import React from 'react';
import { Stack, Tooltip } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { TypographySmallTitle, SectionContainer, NotAssignedTypography } from './DocumentDetailsLeft';
import { IDocumentUnscoped } from '../../../types/my-types';
import SetDocumentDataStatus from './SetDocumentDataStatus';
import DocumentDataStatus from '../../extractionBatches/details/DocumentDataStatus';
import PromoteToParentAction from '../PromoteToParentAction';
import { getDuplicateLineage } from '../documentUtils';
import { DOCUMENT_DATA_STATUS_TOOLTIP } from '../../../constants';

interface IDocumentDataStatusSectionProps {
  documentData: IDocumentUnscoped;
}

const DocumentDataStatusSection: React.FC<IDocumentDataStatusSectionProps> = ({ documentData }) => {
  const hasStatus = documentData.document_data_status;
  // Gate promote-to-parent on the relationship signal (a duplicate child), matching
  // the manifest row — not on status+exclusion_reason, which can diverge.
  const { isDuplicateChild } = getDuplicateLineage(documentData);

  return (
    <SectionContainer>
      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ marginBottom: '5px' }}>
        <TypographySmallTitle>DOCUMENT DATA STATUS</TypographySmallTitle>
        <Tooltip
          title={DOCUMENT_DATA_STATUS_TOOLTIP}
          placement="top"
        >
          <HelpOutlineIcon sx={{ fontSize: 14, color: 'grey.500', cursor: 'help' }} />
        </Tooltip>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {hasStatus ? (
          <DocumentDataStatus
            status={documentData.document_data_status}
            exclusionReason={documentData.exclusion_reason}
            escalationDetail={documentData.escalation_detail}
          />
        ) : (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <WarningIcon sx={{ color: 'orange', fontSize: 'small' }} />
            <NotAssignedTypography data-cy="document-data-status-not-set">
              Not Set
            </NotAssignedTypography>
          </Stack>
        )}
        <Stack direction="row" spacing={0.5} alignItems="center">
          {isDuplicateChild && documentData.document_data_id && (
            <PromoteToParentAction
              documentId={documentData.id}
              documentDataId={documentData.document_data_id}
            />
          )}
          <SetDocumentDataStatus
            documentId={documentData.id}
            documentDataId={documentData.document_data_id}
            currentStatus={documentData.document_data_status}
            currentExclusionReason={documentData.exclusion_reason}
            currentEscalationDetail={documentData.escalation_detail}
          />
        </Stack>
      </Stack>
    </SectionContainer>
  );
};

export default DocumentDataStatusSection;
